import { ticketType } from "@agentcall/core/type"
import type { TicketList200TicketsItemStatus } from "@/api/generated/models"

// Status display labels - use from core
export const ticketStatusLabels = Object.fromEntries(
  ticketType.TICKET_STATUSES.map((item) => [item.key, item.value])
) as Record<ticketType.status, string>

// Category display labels - use from core
export const ticketCategoryLabels = Object.fromEntries(
  ticketType.TICKET_CATEGORIES.map((item) => [item.key, item.value])
) as Record<ticketType.category, string>

// Sub-category display labels - use from core
export const ticketSubCategoryLabels = Object.fromEntries(
  ticketType.TICKET_SUBCATEGORIES.map((item) => [item.key, item.value])
) as Record<ticketType.subCategory, string>

// Contact type display labels - use from core
export const ticketContactTypeLabels = Object.fromEntries(
  ticketType.CONTACT_TYPES.map((item) => [item.key, item.value])
) as Record<ticketType.contactType, string>

// Contact category labels - use from core
export const ticketContactCategoryLabels = Object.fromEntries(
  ticketType.CONTACT_CATEGORIES.map((item) => [item.key, item.value])
) as Record<ticketType.contactFromCategory | ticketType.contactToCategory, string>

// Contractor status display labels - use from core
export const ticketContractorStatusLabels = Object.fromEntries(
  ticketType.CONTRACTOR_STATUSES.map((item) => [item.key, item.value])
) as Record<NonNullable<ticketType.contractorStatus>, string>

// Contractor type display labels - use from core
export const ticketContractorTypeLabels = Object.fromEntries(
  ticketType.CONTRACTOR_TYPES.map((item) => [item.key, item.value])
) as Record<NonNullable<ticketType.contractorType>, string>

// Get status variant for UI components
export function getTicketStatusVariant(
  ticketStatus: ticketType.status | TicketList200TicketsItemStatus
): "oc" | "owner" | "vendor" | "completed" | "default" {
  // Handle both core status type and API response type
  const statusStr = typeof ticketStatus === "string" ? ticketStatus : String(ticketStatus)

  switch (statusStr) {
    case ticketType.TICKET_STATUS_OC_IN_PROGRESS:
      return "oc"
    case ticketType.TICKET_STATUS_OWNER_IN_PROGRESS:
      return "owner"
    case ticketType.TICKET_STATUS_CONTRACTOR_IN_PROGRESS:
      return "vendor"
    case ticketType.TICKET_STATUS_COMPLETED:
      return "completed"
    default:
      return "default"
  }
}

// Get status variant for UI components
export function getTicketStatusLabel(ticketStatus: ticketType.status): string {
  const find = ticketType.TICKET_STATUSES.find((item) => item.key === ticketStatus)
  return find ? find.value : "default"
}

// Get status color for badges/labels
export function getTicketStatusColor(
  ticketStatus: ticketType.status | TicketList200TicketsItemStatus
): string {
  const statusStr = typeof ticketStatus === "string" ? ticketStatus : String(ticketStatus)

  switch (statusStr) {
    case ticketType.TICKET_STATUS_OC_IN_PROGRESS:
      return "bg-orange-100 text-orange-800"
    case ticketType.TICKET_STATUS_OWNER_IN_PROGRESS:
      return "bg-blue-100 text-blue-800"
    case ticketType.TICKET_STATUS_CONTRACTOR_IN_PROGRESS:
      return "bg-cyan-100 text-cyan-800"
    case ticketType.TICKET_STATUS_COMPLETED:
      return "bg-green-100 text-green-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

// Format date for display
export function formatTicketDate(dateString: string | null | undefined): string {
  if (!dateString) return "-"

  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  } catch {
    return dateString
  }
}

// Format short date for display
export function formatTicketShortDate(dateString: string | null | undefined): string {
  if (!dateString) return "-"

  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date)
  } catch {
    return dateString
  }
}

// Get display name for contact
export function getContactDisplayName(
  contactCategory:
    | ticketType.contactFromCategory
    | ticketType.contactToCategory
    | string
    | null
    | undefined,
  owner?: { name?: string | null } | null,
  tenant?: { name?: string | null } | null,
  admin?: { name?: string | null } | null,
  text?: string | null
): string {
  const categoryStr =
    typeof contactCategory === "string" ? contactCategory : String(contactCategory || "")

  // Use the labels from core
  const categoryLabel =
    ticketContactCategoryLabels[categoryStr as keyof typeof ticketContactCategoryLabels]

  switch (categoryStr) {
    case "owner":
      return owner?.name || categoryLabel || "オーナー"
    case "tenant":
      return tenant?.name || categoryLabel || "賃借人"
    case "resident":
      return tenant?.name || categoryLabel || "入居者"
    case "oc":
      return admin?.name || categoryLabel || "OC"
    case "contractor":
      return text || categoryLabel || "業者"
    case "emergency":
      return categoryLabel || "緊急連絡センター"
    case "other":
      return text || categoryLabel || "その他"
    default:
      return "-"
  }
}

// Check if ticket has unread messages
export function hasUnreadMessages(ticket: {
  ticketMessages?: Array<{ isRead?: boolean }> | null
}): boolean {
  return ticket.ticketMessages?.some((message) => !message.isRead) ?? false
}

// Check if ticket is overdue
export function isTicketOverdue(alertAt: string | null | undefined): boolean {
  if (!alertAt) return false

  try {
    const alertDate = new Date(alertAt)
    const now = new Date()
    return alertDate < now
  } catch {
    return false
  }
}

// Sort tickets helper
export function sortTickets<T extends { createdAt: string }>(
  tickets: T[],
  sortBy: "createdAt" | "updatedAt" | "occurredAt" | "alertAt" = "createdAt",
  sortOrder: "asc" | "desc" = "desc"
): T[] {
  return [...tickets].sort((a, b) => {
    // biome-ignore lint/suspicious/noExplicitAny: explanation
    const aValue = (a as any)[sortBy] || ""
    // biome-ignore lint/suspicious/noExplicitAny: explanation
    const bValue = (b as any)[sortBy] || ""

    if (sortOrder === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    }
    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
  })
}

// Filter tickets helper
export function filterTickets<
  T extends {
    status?: string
    category?: string
    propertyId?: number
    roomId?: number | null
    tenantId?: number | null
    ownerId?: string
    ocAssigneeId?: string | null
    title?: string
  },
>(
  tickets: T[],
  filters: {
    status?: string[]
    category?: string[]
    propertyId?: number
    roomId?: number
    tenantId?: number
    ownerId?: string
    ocAssigneeId?: string
    searchTerm?: string
  }
): T[] {
  return tickets.filter((ticket) => {
    // Status filter
    if (filters.status?.length && ticket.status) {
      if (!filters.status.includes(ticket.status)) return false
    }

    // Category filter
    if (filters.category?.length && ticket.category) {
      if (!filters.category.includes(ticket.category)) return false
    }

    // Property filter
    if (filters.propertyId !== undefined && ticket.propertyId !== filters.propertyId) {
      return false
    }

    // Room filter
    if (filters.roomId !== undefined && ticket.roomId !== filters.roomId) {
      return false
    }

    // Tenant filter
    if (filters.tenantId !== undefined && ticket.tenantId !== filters.tenantId) {
      return false
    }

    // Owner filter
    if (filters.ownerId && ticket.ownerId !== filters.ownerId) {
      return false
    }

    // OC Assignee filter
    if (filters.ocAssigneeId && ticket.ocAssigneeId !== filters.ocAssigneeId) {
      return false
    }

    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      const titleMatch = ticket.title?.toLowerCase().includes(searchLower)
      return titleMatch
    }

    return true
  })
}
